import { useState } from 'react';
import { useStripe, useElements } from '@stripe/react-stripe-js';
import { useModals, useSnackbars } from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { IS_STRIPE_TEST_MODE } from '@/services/billing/stripe/elements';
import { useUpdateAccountAddress } from '@/services/db/account/mutations';
import { UpdateAddressModal } from '../components/update-address-modal';
import { addressFromAccount, applyAddressChange, visibleBillingErrors } from '../utils/address-form-helpers';
import { isZipCodeIgnored } from '../utils/address-options';
import { validateBillingAddress } from '../utils/address-validation';
import type { CreditCardFormAddress } from '../utils/credit-card-form-submit';
import { billingFieldErrors, firstBillingError, type BillingFieldErrors } from '../utils/credit-card-form-validation';
import { useBillingEditGuard } from './use-billing-edit-guard';

/** The services a form that charges a card reads. Bundled so each form stays under the hook budget. */
export function useCardChargeServices() {
  const { account } = useAuth();
  const { mutateAsync: updateAccountAddress } = useUpdateAccountAddress({ accountId: account.id });
  return {
    account,
    track: useAnalytics().track,
    snackbars: useSnackbars(),
    modals: useModals(),
    stripe: useStripe(),
    elements: useElements(),
    editGuard: useBillingEditGuard(),
    updateAccountAddress,
  };
}

/** The card and address handlers a form passes to its card fields. */
export type CardEntryHandlers = Pick<
  CardEntryFields,
  'onNameInput' | 'onAddressChange' | 'onFieldBlur' | 'setIsCardComplete'
>;

export type CardEntryFields = {
  name: string;
  address: CreditCardFormAddress;
  fieldErrors: BillingFieldErrors;
  /** The field errors for the fields the user has left. */
  visibleErrors: BillingFieldErrors;
  firstError: string | undefined;
  /** Whether the card can be charged: a saved card, or new card details Stripe reports complete. */
  isCardReady: boolean;
  canEditZip: boolean;
  setAddress: (address: CreditCardFormAddress) => void;
  onNameInput: (value: string) => void;
  onAddressChange: (field: keyof CreditCardFormAddress, value: string) => void;
  onFieldBlur: (field: string) => void;
  /** Consumed by `<CardFields onChange>` to report Stripe card completeness. */
  setIsCardComplete: (complete: boolean) => void;
  /** A saved card bills to the account address; a new card starts with empty card details. */
  onPaymentMethodChange: (useSavedCard: boolean) => void;
};

/**
 * The name, billing address and card-completeness state of a form that charges a card, with the field
 * errors derived from them. A saved card needs none of these fields, so it reports no errors.
 * `onInteract` runs on every change the user makes, for the form's interaction analytics.
 */
export function useCardEntryFields(useSavedCard: boolean, onInteract: () => void): CardEntryFields {
  const { account } = useAuth();
  const [name, setName] = useState('');
  const [address, setAddress] = useState<CreditCardFormAddress>(() => addressFromAccount(account));
  const [isCardComplete, setIsCardComplete] = useState(false);
  const [touched, setTouched] = useState<ReadonlySet<string>>(() => new Set());

  const fieldErrors = useSavedCard ? {} : billingFieldErrors(name, address);

  return {
    name,
    address,
    fieldErrors,
    visibleErrors: visibleBillingErrors(fieldErrors, touched),
    firstError: firstBillingError(fieldErrors),
    // Stripe can't report completeness in test mode (plain inputs), so treat the card as complete then.
    isCardReady: useSavedCard || IS_STRIPE_TEST_MODE || isCardComplete,
    canEditZip: !isZipCodeIgnored(address.country),
    setAddress,
    onNameInput: (value) => {
      onInteract();
      setName(value);
    },
    onAddressChange: (field, value) => {
      onInteract();
      setAddress((current) => applyAddressChange(current, field, value));
    },
    onFieldBlur: (field) => setTouched((current) => new Set(current).add(field)),
    setIsCardComplete: (complete) => {
      onInteract();
      setIsCardComplete(complete);
    },
    onPaymentMethodChange: (nextUseSavedCard) => {
      if (nextUseSavedCard) {
        setAddress(addressFromAccount(account));
      } else {
        setIsCardComplete(false);
      }
    },
  };
}

/**
 * Checks a billing address with the address validator. On a suggested correction it asks the user to
 * pick one, and returns the address chosen, or `'aborted'` when the user dismisses the modal.
 */
export async function chooseBillingAddress(
  accountId: number,
  modals: Pick<ReturnType<typeof useModals>, 'open' | 'waitForClose'>,
  input: CreditCardFormAddress,
): Promise<CreditCardFormAddress | 'aborted'> {
  const validation = await validateBillingAddress(accountId, input);
  if (validation?.correctedAddress == null) return input;

  const modalId = modals.open(UpdateAddressModal, {
    inputAddress: input,
    correctedAddress: validation.correctedAddress,
  });
  const result = await modals.waitForClose(modalId);
  return result.reason === 'confirm' && result.data != null ? result.data : 'aborted';
}

/** The snackbar a form shows when the user dismisses the address correction without picking one. */
export const CORRECTED_ADDRESS_MESSAGE =
  'We found a possible correction for your address. Select the current address or corrected address.';
